import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import {
    loadSettings,
    saveSettings,
    defaultSettingValues,
    resolveCalculationSettings
} from './settingsStore';

/**
 * Shared calculation settings.
 *
 * Mounted once at the app root so the calculator, the wizard and a customer
 * quote are all priced on the same calibration. `effective` is the appConfig-
 * shaped object the pricing engine consumes.
 */
const SettingsContext = createContext(null);

export const SettingsProvider = ({ children }) => {
    const [values, setValues] = useState(defaultSettingValues);
    const [meta, setMeta] = useState({ source: 'loading', version: null });

    useEffect(() => {
        let cancelled = false;
        loadSettings().then(result => {
            if (cancelled) return;
            setValues(result.values);
            setMeta({
                source: result.source,
                version: result.version,
                updatedBy: result.updatedBy,
                updatedAt: result.updatedAt,
                reason: result.reason
            });
        });
        return () => { cancelled = true; };
    }, []);

    const save = useCallback(async (nextValues, saveMeta) => {
        setValues(nextValues);
        const result = await saveSettings(nextValues, saveMeta);
        setMeta(prev => ({
            ...prev,
            source: result.persisted ? 'database' : 'local',
            version: result.version ?? prev.version,
            updatedAt: result.updatedAt,
            updatedBy: saveMeta?.updatedBy,
            reason: result.reason
        }));
        return result;
    }, []);

    const reset = useCallback(() => setValues(defaultSettingValues()), []);

    // Re-inflating is cheap but runs on every price, so it is memoised.
    const effective = useMemo(() => resolveCalculationSettings(values), [values]);

    const api = useMemo(
        () => ({ values, setValues, effective, meta, save, reset }),
        [values, effective, meta, save, reset]
    );

    return <SettingsContext.Provider value={api}>{children}</SettingsContext.Provider>;
};

/** Never throws when unmounted — falls back to the compiled defaults. */
export const useSettings = () => {
    const ctx = useContext(SettingsContext);
    if (ctx) return ctx;
    const values = defaultSettingValues();
    return {
        values,
        setValues: () => {},
        effective: resolveCalculationSettings(values),
        meta: { source: 'defaults', version: null },
        save: async () => ({ ok: false, persisted: false }),
        reset: () => {}
    };
};

export default SettingsContext;
